/* site/edit/EditContext.jsx — the visual editor's session.
 *
 * Holds the DRAFT (an overrides object keyed exactly like the stored site content), applies it on
 * top of the saved overrides so every edit renders instantly on the real page, and saves the whole
 * thing back through the existing PUT /api/admin/site/content.
 *
 * Edit mode turns on when the page is opened with ?edit=1 AND the visitor is a signed-in SUPERADMIN
 * (verified against /api/auth/me — the query string alone proves nothing). Everyone else gets the
 * ordinary public site, so a shared "?edit=1" link is harmless.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { setAt, getAt } from "./editPaths";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
export const EDIT_PARAM = "edit";

const Ctx = createContext(null);
export const useEditSession = () => useContext(Ctx);

const token = () => { try { return localStorage.getItem("lhc_token"); } catch (e) { return null; } };
const auth = () => ({ headers: { Authorization: `Bearer ${token()}` } });

/** ?edit=1 in the address bar (urlMask keeps the real url in history.state) */
export function wantsEdit(search) {
  try {
    const q = new URLSearchParams(search != null ? search : window.location.search);
    return q.get(EDIT_PARAM) === "1";
  } catch (e) { return false; }
}

export function EditProvider({ children }) {
  const [active, setActive] = useState(false);
  const [user, setUser] = useState(null);
  const [draft, setDraft] = useState({});          // overrides being edited, not yet saved
  const [saved, setSaved] = useState({});          // what the server already has
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const history = useRef([]);                      // undo stack of previous drafts

  /* gate: the flag AND a superadmin session */
  useEffect(() => {
    if (!wantsEdit() || !token()) return undefined;
    let alive = true;
    axios.get(`${API}/auth/me`, auth())
      .then((r) => {
        const u = r.data || {};
        const role = String(u.role || u.user_role || "").toUpperCase();
        if (!alive) return;
        if (role === "SUPERADMIN" || role === "ADMIN") { setUser(u); setActive(true); }
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  /* what the server currently stores — the draft is layered over this */
  useEffect(() => {
    if (!active) return undefined;
    let alive = true;
    axios.get(`${API}/site/content`)
      .then((r) => { if (alive) setSaved((r.data && r.data.content) || {}); })
      .catch(() => {});
    return () => { alive = false; };
  }, [active]);

  /* `base` is the merged content the editor is looking at — setAt needs it so that touching one
     item of a list keeps the rest of the list (arrays replace on merge, they do not deep-merge). */
  const setValue = useCallback((path, value, base) => {
    setDraft((d) => {
      history.current.push(d);
      if (history.current.length > 100) history.current.shift();
      return setAt(d, path, value, base);
    });
    setStatus("");
  }, []);

  const undo = useCallback(() => {
    setDraft((d) => (history.current.length ? history.current.pop() : d));
  }, []);

  const discard = useCallback(() => { history.current = []; setDraft({}); setStatus("Changes discarded"); }, []);

  const save = useCallback(async () => {
    setBusy(true);
    setStatus("");
    try {
      const merged = mergeDeep(saved, draft);
      await axios.put(`${API}/admin/site/content`, { content: merged }, auth());
      setSaved(merged);
      history.current = [];
      setDraft({});
      setStatus("Saved — live on the site");
    } catch (e) {
      const detail = e && e.response && e.response.data && e.response.data.detail;
      setStatus(detail || "Could not save");
    }
    setBusy(false);
  }, [saved, draft]);

  const dirty = useMemo(() => Object.keys(draft).length > 0, [draft]);

  const value = useMemo(() => ({
    active, user, draft, saved, dirty, busy, status, setValue, undo, discard, save,
    valueAt: (path) => { const d = getAt(draft, path); return d === undefined ? getAt(saved, path) : d; },
  }), [active, user, draft, saved, dirty, busy, status, setValue, undo, discard, save]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

const isObj = (v) => v && typeof v === "object" && !Array.isArray(v);
/** same rule as the content layer: objects merge, arrays and scalars replace */
export function mergeDeep(base, over) {
  if (over === undefined) return base;
  if (isObj(base) && isObj(over)) {
    const out = { ...base };
    Object.keys(over).forEach((k) => { out[k] = mergeDeep(base[k], over[k]); });
    return out;
  }
  return over;
}
